/**
 * Selector de parqueadero para la cabecera del sidebar.
 *
 * Antes solo se podía elegir parqueadero al iniciar sesión: para cambiar
 * había que cerrar sesión y volver a entrar. Aquí se elige desde dentro, con
 * buscador para cuando la lista es larga.
 *
 * Cada parqueadero muestra debajo las sucursales que le corresponden a este
 * usuario (con la etiqueta "Sucursal"): elegir una entra a esa sucursal —con
 * la pantalla que corresponde a tu rol— y siempre aterriza en el Dashboard.
 * Si el rol tiene modo global (admin/supervisor/superadmin/franquiciado), el
 * menú desplegable también ofrece "Volver a global" arriba de la lista; la
 * acción principal para eso vive en el encabezado (ver UserHeader), no aquí.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Popover, TextField, List, ListItemButton,
  CircularProgress, InputAdornment, alpha, Tooltip, Chip, Divider,
} from '@mui/material';
import { LocalParking, Search, ExpandMore, Check, Storefront, Public } from '@mui/icons-material';
import apiClient from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigation, VIEWS } from '../../context/NavigationContext';

const ORANGE = '#F59E0B';
const BLUE   = '#3b82f6';
const WHITE  = '#FFFFFF';

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_API_URL || ''}${url}`;
};

function Logo({ url, size }) {
  return url
    ? <Box component="img" src={resolveUrl(url)} alt=""
        sx={{ width: size, height: size, borderRadius: 1.5, objectFit: 'contain', bgcolor: 'white', p: 0.25, flexShrink: 0 }} />
    : <Box sx={{ width: size, height: size, borderRadius: 1.5, bgcolor: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <LocalParking sx={{ color: WHITE, fontSize: size * 0.55 }} />
      </Box>;
}

export function BadgeSucursal({ sx }) {
  return (
    <Chip size="small" label="Sucursal" icon={<Storefront sx={{ fontSize: '11px !important' }} />}
      sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: alpha(BLUE, 0.14), color: BLUE,
            '& .MuiChip-icon': { color: BLUE, ml: 0.5 }, ...sx }} />
  );
}

export default function SelectorParqueadero({ abierto }) {
  const { user, clienteInfo, selectCliente } = useAuth();
  const { navigate } = useNavigation();
  const [anchor, setAnchor]   = useState(null);
  const [lista, setLista]     = useState([]);
  const [puedeGlobal, setPuedeGlobal] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [filtro, setFiltro]   = useState('');
  const [cambiando, setCambiando] = useState(null);

  // Se consulta al abrir: la lista del login se pierde al recargar la página
  useEffect(() => {
    if (!anchor) return;
    setCargando(true);
    apiClient.get('/api/auth/mis-parqueaderos')
      .then(res => { setLista(res.data?.data || []); setPuedeGlobal(!!res.data?.puede_modo_global); })
      .catch(() => setLista([]))
      .finally(() => setCargando(false));
  }, [anchor]);

  const sucursalActual = clienteInfo?.sucursal || null;
  const nombreActual = clienteInfo?.nombre || process.env.REACT_APP_NOMBRE_CONJUNTO || 'PARKEY';
  const idActual = user?.cliente_id;
  const sucursalIdActual = user?.sucursal_id ?? null;
  const tituloCompleto = `${nombreActual}${sucursalActual ? ` · ${sucursalActual.nombre}` : ' · Todas las sucursales'}`;
  const q = filtro.toLowerCase();
  const coincide = (t) => (t || '').toLowerCase().includes(q);
  // Un parqueadero se muestra si coincide él o alguna de sus sucursales; sus
  // sucursales, si coinciden o si coincidió el parqueadero.
  const visibles = lista
    .map(c => {
      const coincideCliente = coincide(c.nombre) || coincide(c.slug);
      const sucursales = (c.sucursales || []).filter(s => coincideCliente || coincide(s.nombre));
      return { ...c, sucursales, mostrar: coincideCliente || sucursales.length > 0 };
    })
    .filter(c => c.mostrar);

  const abrir = (e) => { setFiltro(''); setAnchor(e.currentTarget); };
  const cerrar = () => { setAnchor(null); setFiltro(''); };

  const esActual = (clienteId, sucursalId) => {
    if (clienteId !== idActual) return false;
    return sucursalId == null ? sucursalIdActual == null : sucursalIdActual === sucursalId;
  };

  // sucursalId: número = entra a esa sucursal; null = modo global explícito.
  const elegir = async (clienteId, sucursalId) => {
    if (esActual(clienteId, sucursalId)) { cerrar(); return; }
    setCambiando(`${clienteId}:${sucursalId ?? ''}`);
    try {
      const res = await selectCliente(clienteId, sucursalId);
      if (res.success) {
        cerrar();
        // Entrar a cualquier contexto (sucursal o global) siempre aterriza
        // en el Dashboard de ese contexto, sin importar desde dónde se eligió.
        navigate(VIEWS.DASHBOARD);
      }
    } finally {
      setCambiando(null);
    }
  };

  const Fila = ({ c, s = null, global: esGlobal = false }) => {
    const actual = esActual(c.id, s?.id ?? null);
    const clave = `${c.id}:${s?.id ?? ''}`;
    const nombre = esGlobal ? 'Todas las sucursales' : (s ? s.nombre : c.nombre);
    return (
      <ListItemButton onClick={() => elegir(c.id, s?.id ?? null)} disabled={cambiando !== null}
        sx={{ gap: 1.5, py: 1, px: 1.5, pl: (s || esGlobal) ? 4.5 : 1.5, bgcolor: actual ? alpha(ORANGE, 0.08) : 'transparent' }}>
        {s
          ? <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: alpha(BLUE, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Storefront sx={{ fontSize: 16, color: BLUE }} />
            </Box>
          : esGlobal
            ? <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: alpha(ORANGE, 0.14), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Public sx={{ fontSize: 16, color: ORANGE }} />
              </Box>
            : <Logo url={c.logo_url} size={28} />}
        {/* minWidth 0 es lo que permite que noWrap recorte: sin eso el flex
            hijo crece con el texto y desborda la fila. flex:1 real (no fijo
            por el chip) para que el nombre tenga todo el ancho disponible. */}
        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <Tooltip title={nombre.length > 22 ? nombre : ''} placement="top" arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={actual ? 700 : 500} noWrap
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {nombre}
              </Typography>
              {s && <BadgeSucursal sx={{ flexShrink: 0 }} />}
            </Box>
          </Tooltip>
          {!esGlobal && (
            <Typography variant="caption" noWrap
              sx={{ color: '#999', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s ? `de ${c.nombre}` : (c.slug || '')}
            </Typography>
          )}
        </Box>
        {cambiando === clave
          ? <CircularProgress size={16} sx={{ color: ORANGE }} />
          : actual && <Check sx={{ fontSize: 18, color: ORANGE }} />}
      </ListItemButton>
    );
  };

  return (
    <>
      <Tooltip title={tituloCompleto} placement="right" arrow>
        <Box onClick={abrir}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0,
            cursor: 'pointer', borderRadius: 2, p: abierto ? 0.5 : 0,
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: alpha(WHITE, 0.08) },
          }}>
          <Logo url={clienteInfo?.logo_url} size={abierto ? 36 : 34} />
          {abierto && (
            <>
              {/* Dentro de una sucursal, SU nombre es lo relevante: pasa a
                  ser la línea principal (negrita, todo el ancho disponible),
                  y el parqueadero baja a subtítulo — antes era al revés y el
                  nombre de la sucursal competía por espacio con un chip,
                  quedando recortado a una letra. */}
              <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <Typography variant="caption" fontWeight={700} noWrap
                  sx={{ color: WHITE, display: 'block', lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {sucursalActual ? sucursalActual.nombre : nombreActual}
                </Typography>
                <Typography variant="caption" noWrap
                  sx={{ color: alpha(WHITE, 0.55), fontSize: 10.5, display: 'block', mt: 0.25,
                        overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {sucursalActual ? nombreActual : 'Todas las sucursales'}
                </Typography>
              </Box>
              <ExpandMore sx={{ color: alpha(WHITE, 0.45), fontSize: 18, flexShrink: 0 }} />
            </>
          )}
        </Box>
      </Tooltip>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={cerrar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: 3, width: 340, mt: 0.5, overflow: 'hidden' } }}
      >
        {/* Acción fija, siempre visible arriba: volver a ver todas las
            sucursales combinadas. Solo para roles con modo global, y solo
            tiene sentido si ahora mismo se está dentro de una sucursal. */}
        {puedeGlobal && sucursalIdActual != null && (
          <>
            <ListItemButton onClick={() => elegir(idActual, null)} disabled={cambiando !== null}
              sx={{ gap: 1.5, py: 1.1, px: 1.5 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: alpha(ORANGE, 0.16), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Public sx={{ fontSize: 16, color: ORANGE }} />
              </Box>
              <Typography variant="body2" fontWeight={700} sx={{ color: ORANGE }}>
                Volver a global
              </Typography>
              {cambiando === `${idActual}:` && <CircularProgress size={16} sx={{ color: ORANGE, ml: 'auto' }} />}
            </ListItemButton>
            <Divider />
          </>
        )}

        <Box sx={{ p: 1.5, borderBottom: '1px solid #EEE' }}>
          <TextField
            autoFocus fullWidth size="small" placeholder="Buscar parqueadero o sucursal..."
            value={filtro} onChange={e => setFiltro(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
              sx: { borderRadius: 2 },
            }}
          />
        </Box>

        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} sx={{ color: ORANGE }} />
          </Box>
        ) : visibles.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#999' }}>
              {lista.length === 0 ? 'No hay parqueaderos disponibles' : 'Ninguno coincide con la búsqueda'}
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 360, overflowY: 'auto', py: 0.5 }}>
            {visibles.map(c => (
              <React.Fragment key={c.id}>
                {/* El parqueadero en sí (logo + nombre) solo es una fila
                    elegible si el rol tiene modo global — para el resto es
                    apenas un encabezado agrupador de sus sucursales. */}
                {puedeGlobal
                  ? <Fila c={c} global={c.sucursales.length > 0} />
                  : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5 }}>
                      <Logo url={c.logo_url} size={28} />
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0, flex: 1 }}>
                        {c.nombre}
                      </Typography>
                    </Box>
                  )}
                {c.sucursales.map(s => <Fila key={s.id} c={c} s={s} />)}
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
